import type { BaseTextData } from '@/teoCore/TeoStorage';

import type { AnalysisJSON } from '../AnalysisResult';
import type { TeoText } from '../TeoText';

export interface HistoryData extends Omit<BaseTextData, 'lastEdited'> {
  lastEdited: number;
  analysis: AnalysisJSON;
}

export class HistoryEntry {
  #data: HistoryData;
  #text: TeoText;

  static create(text: TeoText) {
    if (!text.state.allPromptOptionsSelected)
      throw new Error('Missing text configuration for history entry');
    const data = text.getBaseTextData();

    if (!data.analysis) throw new Error('Missing analysis for history entry');
    if (!data.lastEdited) {
      data.lastEdited = Date.now();
    }

    return new HistoryEntry(text, data as HistoryData);
  }

  /**
   * Creates a new history entry.
   * @param text The text to create the entry for.
   * @param virtual If true, the entry is not part of the history and is only used to restore the text.
   */
  protected constructor(text: TeoText, data: HistoryData) {
    this.#text = text;
    this.#data = data;
  }

  get value() {
    return this.#data.analysis.data.tpi;
  }

  get date() {
    return this.#data.lastEdited;
  }

  restore() {
    this.#text.history.restore(this.#data);
  }

  select() {
    this.#text.history.select(this.#data);
  }

  setTextDiff(show = true) {
    this.#text.diff.update(this.#data.text, show);
  }

  toJSON(): HistoryData {
    return this.#data;
  }

  static fromJSON(text: TeoText, json: HistoryData) {
    const entry = new HistoryEntry(text, json);
    entry.#data = json;

    return entry;
  }
}
